import jsQR from 'jsqr';
import QRCode from 'qrcode';

export interface ProcessedQrResult {
  svgString: string;
  svgDataUrl: string;
  qrPayload: string;
}

export interface QrSvgOptions {
  colorDark?: string;
  colorLight?: string;
  margin?: number;
}

export const DEFAULT_PERSATUAN_QR_PAYLOAD = '00020101021126580014my.com.duitnow01189330000201101008765402030005204599953034585802MY5940PERSATUAN SENI SILAT AWANG SULUNG MERAH6005KEDAH6304';

// In-memory cache for ultra-fast instant lookups (0ms)
const qrProcessingCache = new Map<string, ProcessedQrResult>();

/**
 * Loads an image source (File, Blob, or URL/DataURL string) into an HTMLImageElement with a fast timeout
 */
function loadImageElement(source: File | Blob | string): Promise<{ img: HTMLImageElement; cleanup: () => void }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    let objectUrl: string | null = null;
    const cleanup = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

    // Fast network timeout to avoid hanging on missing placeholder paths
    const timeoutTimer = setTimeout(() => {
      cleanup();
      reject(new Error('Masa muat turun imej kod QR tamat.'));
    }, 1200);

    img.onload = () => {
      clearTimeout(timeoutTimer);
      resolve({ img, cleanup });
    };

    img.onerror = () => {
      clearTimeout(timeoutTimer);
      cleanup();
      reject(new Error('Gagal memuat naik atau memproses fail imej kod QR.'));
    };

    if (typeof source === 'string') {
      img.src = source;
    } else {
      objectUrl = URL.createObjectURL(source);
      img.src = objectUrl;
    }
  });
}

/**
 * Decodes a QR code payload from an image using jsQR with multi-stage enhancement:
 * 1. Direct scan (downscaled to max 800px for 4x faster processing)
 * 2. Grayscale & Contrast-stretch binarization (ideal for DuitNow pink / red on white/gray)
 * 3. Center crop scan (handles bank app screenshots with surrounding headers/buttons)
 */
export async function decodeQrFromImage(source: File | Blob | string): Promise<{ data: string; width: number; height: number } | null> {
  try {
    const { img, cleanup } = await loadImageElement(source);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      cleanup();
      return null;
    }

    // Limit maximum dimension to 800px for ultra-fast processing
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;

    if (width === 0 || height === 0) {
      cleanup();
      return null;
    }

    const maxDim = 800;
    if (width > maxDim || height > maxDim) {
      const scale = maxDim / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);
    cleanup();

    // Stage 1: Direct jsQR scan
    const rawImageData = ctx.getImageData(0, 0, width, height);
    let code = jsQR(rawImageData.data, width, height, { inversionAttempts: 'attemptBoth' });
    if (code && code.data && code.data.trim()) {
      return { data: code.data.trim(), width, height };
    }

    // Stage 2: Grayscale & Contrast Binarization
    // Especially helpful for pink DuitNow QR codes (e.g. #ED0080) on tinted cards
    const binarized = ctx.createImageData(width, height);
    const srcData = rawImageData.data;
    const dstData = binarized.data;

    let minLum = 255;
    let maxLum = 0;
    const lumArray = new Uint8Array(width * height);

    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) {
      const r = srcData[i];
      const g = srcData[i + 1];
      const b = srcData[i + 2];
      const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      lumArray[p] = lum;
      if (lum < minLum) minLum = lum;
      if (lum > maxLum) maxLum = lum;
    }

    const threshold = (minLum + maxLum) / 2;

    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) {
      const val = lumArray[p] < threshold ? 0 : 255;
      dstData[i] = val;
      dstData[i + 1] = val;
      dstData[i + 2] = val;
      dstData[i + 3] = 255;
    }

    code = jsQR(dstData, width, height, { inversionAttempts: 'attemptBoth' });
    if (code && code.data && code.data.trim()) {
      return { data: code.data.trim(), width, height };
    }

    // Stage 3: Center Crop (many mobile screenshots place the QR code in the middle 70%)
    const cropW = Math.round(width * 0.70);
    const cropH = Math.round(height * 0.70);
    const cropX = Math.round((width - cropW) / 2);
    const cropY = Math.round((height - cropH) / 2);

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropW;
    cropCanvas.height = cropH;
    const cropCtx = cropCanvas.getContext('2d', { willReadFrequently: true });

    if (cropCtx) {
      cropCtx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      const cropData = cropCtx.getImageData(0, 0, cropW, cropH);
      code = jsQR(cropData.data, cropW, cropH, { inversionAttempts: 'attemptBoth' });
      if (code && code.data && code.data.trim()) {
        return { data: code.data.trim(), width: cropW, height: cropH };
      }
    }

    return null;
  } catch (err) {
    console.warn('QR scan notice (fast fallback):', err);
    return null;
  }
}

/**
 * Generates a clean 1:1 SVG representation of the decoded QR payload.
 * Default color is DuitNow pink (#ED0080) with clean edges and zero distortion.
 */
export async function generate1to1SvgQr(
  data: string,
  options?: QrSvgOptions
): Promise<string> {
  const darkColor = options?.colorDark || '#ED0080';
  const lightColor = options?.colorLight || '#FFFFFF';
  const margin = options?.margin ?? 1;

  const rawSvg = await QRCode.toString(data, {
    type: 'svg',
    margin,
    color: {
      dark: darkColor,
      light: lightColor
    }
  });

  // Guarantee strict 1:1 square ratio attributes and responsive scaling
  return rawSvg.replace(
    '<svg ',
    '<svg preserveAspectRatio="xMidYMid meet" style="aspect-ratio: 1 / 1; width: 100%; height: 100%;" '
  );
}

/**
 * Generates a data URL for the 1:1 SVG
 */
export async function generate1to1SvgDataUrl(
  data: string,
  options?: QrSvgOptions
): Promise<string> {
  const svg = await generate1to1SvgQr(data, options);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * High-level processor: takes an uploaded image, extracts the QR code,
 * and regenerates it into a pure 1:1 SVG format with both SVG string and DataURL.
 * Highly optimized with in-memory caching and instant SVG short-circuit.
 */
export async function processUploadedQrToSvg(
  source: File | Blob | string,
  options?: QrSvgOptions
): Promise<ProcessedQrResult | null> {
  // 1. Instant cache hit for string sources
  if (typeof source === 'string') {
    if (qrProcessingCache.has(source)) {
      return qrProcessingCache.get(source)!;
    }

    // 2. Direct instant parsing if source is already an SVG string or SVG data URL (0ms)
    const isSvg = source.startsWith('data:image/svg+xml') || source.trim().startsWith('<svg');
    if (isSvg) {
      const svgString = source.trim().startsWith('<svg')
        ? source.trim()
        : decodeURIComponent(source.replace(/data:image\/svg\+xml;charset=utf-8,/, '').replace(/data:image\/svg\+xml,/, ''));
      const svgDataUrl = source.startsWith('data:image/svg+xml')
        ? source
        : `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
      const result: ProcessedQrResult = {
        svgString,
        svgDataUrl,
        qrPayload: ''
      };
      qrProcessingCache.set(source, result);
      return result;
    }
  }

  // 3. Decode raster image
  const decoded = await decodeQrFromImage(source);
  if (!decoded || !decoded.data) {
    // If decoding failed on placeholder URL, instantly generate the official persatuan DuitNow QR
    if (typeof source === 'string' && (source.includes('qr-persatuan') || source.includes('asmm') || source === '')) {
      const fallbackSvg = await generate1to1SvgQr(DEFAULT_PERSATUAN_QR_PAYLOAD, options);
      const fallbackResult: ProcessedQrResult = {
        svgString: fallbackSvg,
        svgDataUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(fallbackSvg)}`,
        qrPayload: DEFAULT_PERSATUAN_QR_PAYLOAD
      };
      qrProcessingCache.set(source, fallbackResult);
      return fallbackResult;
    }
    return null;
  }

  const svgString = await generate1to1SvgQr(decoded.data, options);
  const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

  const result: ProcessedQrResult = {
    svgString,
    svgDataUrl,
    qrPayload: decoded.data
  };

  if (typeof source === 'string') {
    qrProcessingCache.set(source, result);
  }

  return result;
}
